// Composes a branded, shareable archetype card on a canvas and returns a PNG blob.
// Uses a same-origin backend proxy for the archetype image to avoid canvas taint.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const proxied = (url) => `${API}/share/proxy?url=${encodeURIComponent(url)}`;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const tr = w / h;
  let sw, sh, sx, sy;
  if (ir > tr) {
    sh = img.height;
    sw = sh * tr;
    sx = (img.width - sw) / 2;
    sy = 0;
  } else {
    sw = img.width;
    sh = sw / tr;
    sx = 0;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function wrapLines(ctx, text, maxWidth) {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

export async function generateShareBlob(result) {
  try {
    await document.fonts.ready;
  } catch (e) {}

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background
  ctx.fillStyle = "#050505";
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, H * 0.34, 40, W / 2, H * 0.34, 760);
  glow.addColorStop(0, "rgba(59,42,72,0.55)");
  glow.addColorStop(1, "rgba(5,5,5,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Image
  const imgX = 70;
  const imgY = 78;
  const imgW = W - 140;
  const imgH = 620;
  try {
    const img = await loadImage(proxied(result.image));
    ctx.save();
    roundRect(ctx, imgX, imgY, imgW, imgH, 26);
    ctx.clip();
    drawCover(ctx, img, imgX, imgY, imgW, imgH);
    const fade = ctx.createLinearGradient(0, imgY + imgH - 220, 0, imgY + imgH);
    fade.addColorStop(0, "rgba(5,5,5,0)");
    fade.addColorStop(1, "rgba(5,5,5,0.95)");
    ctx.fillStyle = fade;
    ctx.fillRect(imgX, imgY + imgH - 220, imgW, 220);
    ctx.restore();
  } catch (e) {
    ctx.fillStyle = "#0a0a0a";
    roundRect(ctx, imgX, imgY, imgW, imgH, 26);
    ctx.fill();
  }

  // Element badge
  ctx.font = '500 24px Outfit, sans-serif';
  try {
    ctx.letterSpacing = "4px";
  } catch (e) {}
  const badge = (result.element || "").toUpperCase();
  const bw = ctx.measureText(badge).width + 44;
  ctx.strokeStyle = "rgba(197,160,89,0.6)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, imgX + imgW - bw - 20, imgY + 22, bw, 46, 23);
  ctx.stroke();
  ctx.fillStyle = "#C5A059";
  ctx.textBaseline = "middle";
  ctx.fillText(badge, imgX + imgW - bw - 20 + 22, imgY + 22 + 24);
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {}

  // Overline
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "rgba(197,160,89,0.85)";
  ctx.font = '400 22px Outfit, sans-serif';
  try {
    ctx.letterSpacing = "6px";
  } catch (e) {}
  ctx.fillText("YOU DREW", imgX, imgY + imgH + 66);
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {}

  // Name
  ctx.fillStyle = "#C5A059";
  ctx.font = '300 92px "Cormorant Garamond", serif';
  ctx.fillText(result.name, imgX - 2, imgY + imgH + 158);

  // Title
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = '300 26px Outfit, sans-serif';
  try {
    ctx.letterSpacing = "3px";
  } catch (e) {}
  ctx.fillText((result.title || "").toUpperCase(), imgX, imgY + imgH + 200);
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {}

  // Statement (italic, wrapped)
  ctx.fillStyle = "rgba(255,255,255,0.78)";
  ctx.font = 'italic 300 34px "Cormorant Garamond", serif';
  const lines = wrapLines(ctx, "\u201C" + result.statement + "\u201D", imgW);
  let ty = imgY + imgH + 256;
  const lh = 46;
  for (const ln of lines.slice(0, 6)) {
    ctx.fillText(ln, imgX, ty);
    ty += lh;
  }

  // Footer branding
  ctx.textAlign = "center";
  ctx.fillStyle = "rgba(197,160,89,0.9)";
  ctx.font = '400 22px Outfit, sans-serif';
  try {
    ctx.letterSpacing = "8px";
  } catch (e) {}
  ctx.fillText("SYBILS SOCIETY", W / 2, H - 74);
  try {
    ctx.letterSpacing = "3px";
  } catch (e) {}
  ctx.fillStyle = "rgba(255,255,255,0.35)";
  ctx.font = '300 18px Outfit, sans-serif';
  ctx.fillText("THE RETURN OF THE MATRIARCHS", W / 2, H - 44);
  ctx.textAlign = "left";
  try {
    ctx.letterSpacing = "0px";
  } catch (e) {}

  // Border frame
  ctx.strokeStyle = "rgba(197,160,89,0.35)";
  ctx.lineWidth = 2;
  roundRect(ctx, 26, 26, W - 52, H - 52, 22);
  ctx.stroke();

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
}
